import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  InformationCircleIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
// @ts-expect-error react-cytoscapejs has no official types
import CytoscapeComponent from "react-cytoscapejs";
import { Core, ElementDefinition } from "cytoscape";
import { pathwayExplorerClient } from "../../services/PathwayExplorerClient";
import type { OneHopNeighborEdge } from "../../services/PathwayExplorerClient";
import { NetworkData } from "../../hooks/useNetworkData";
import { useTheme } from "../../theme/useTheme";

interface KShortestPathsProps {
  geneList: string[];
  edgeType: string;
  minEdgeScore: number;
}

const MIN_PATH_LENGTH = 1;
const MAX_PATH_LENGTH = 10;

/** Build network from pathEdges: nodes from all path nodes, edges from consecutive pairs in each path. */
function buildNetworkDataFromEdges(data: OneHopNeighborEdge[]): NetworkData {
  const nodeSet = new Set<string>();
  const edgeKeySet = new Set<string>();
  const edges: { source: string; target: string; path: string[]; path_length: number }[] = [];

  data.forEach((edge) => {
    const path = Array.isArray(edge.path) ? edge.path.map((id) => String(id).trim()).filter(Boolean) : [];
    const pathLen = Number(edge.path_length);
    const source =
      (edge.source != null && String(edge.source).trim()) ||
      (path.length > 0 ? path[0] : "");
    const target =
      (edge.target != null && String(edge.target).trim()) ||
      (path.length > 0 ? path[path.length - 1] : "");

    path.forEach((id) => nodeSet.add(id));
    if (source) nodeSet.add(source);
    if (target) nodeSet.add(target);

    if (path.length >= 2) {
      for (let i = 0; i < path.length - 1; i++) {
        const a = path[i];
        const b = path[i + 1];
        if (a && b && a !== b) {
          const key = a < b ? `${a}\t${b}` : `${b}\t${a}`;
          if (!edgeKeySet.has(key)) {
            edgeKeySet.add(key);
            edges.push({ source: a, target: b, path, path_length: pathLen });
          }
        }
      }
    } else if (source && target && source !== target) {
      const key = source < target ? `${source}\t${target}` : `${target}\t${source}`;
      if (!edgeKeySet.has(key)) {
        edgeKeySet.add(key);
        edges.push({ source, target, path, path_length: pathLen });
      }
    }
  });

  const nodes = Array.from(nodeSet).map((gene) => ({ id: gene, label: gene }));
  return { nodes, edges };
}

export const KShortestPaths: React.FC<KShortestPathsProps> = ({
  geneList,
  edgeType,
  minEdgeScore,
}) => {
  const themeCtx = useTheme();
  const cyRef = useRef<Core | null>(null);
  const pathGraphContainerRef = useRef<HTMLDivElement>(null);
  const pathGraphFullscreenWrapperRef = useRef<HTMLDivElement>(null);
  const pathTableFullscreenRef = useRef<HTMLDivElement>(null);
  const resultsSectionRef = useRef<HTMLDivElement>(null);
  const layoutInitialized = useRef(false);
  const prevLoadingPathsRef = useRef(false);
  const pathGraphWasFullscreenRef = useRef(false);
  const [isPathGraphFullscreen, setIsPathGraphFullscreen] = useState(false);
  const [isPathTableFullscreen, setIsPathTableFullscreen] = useState(false);
  const [pathTableSearchQuery, setPathTableSearchQuery] = useState("");

  useEffect(() => {
    const onFullscreenChange = () => {
      const el = document.fullscreenElement;
      if (el === pathGraphFullscreenWrapperRef.current) {
        pathGraphWasFullscreenRef.current = true;
        setIsPathGraphFullscreen(true);
        requestAnimationFrame(() => {
          setTimeout(() => {
            const cy = cyRef.current;
            if (cy) {
              cy.resize();
              cy.fit(undefined, 30);
            }
          }, 100);
        });
      } else if (el === null) {
        if (pathGraphWasFullscreenRef.current) {
          pathGraphWasFullscreenRef.current = false;
          requestAnimationFrame(() => {
            setTimeout(() => {
              const cy = cyRef.current;
              if (cy) {
                cy.resize();
                cy.fit(undefined, 30);
              }
              pathGraphFullscreenWrapperRef.current?.focus();
            }, 100);
          });
        }
        setIsPathGraphFullscreen(false);
      } else {
        pathGraphWasFullscreenRef.current = false;
        setIsPathGraphFullscreen(false);
      }
      setIsPathTableFullscreen(el === pathTableFullscreenRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, [cyRef]);

  const [selectedGenes, setSelectedGenes] = useState<Set<string>>(new Set());
  const [maxPathLength, setMaxPathLength] = useState<number>(2);
  const [pathEdges, setPathEdges] = useState<OneHopNeighborEdge[] | null>(null);
  const [pathResultMaxLength, setPathResultMaxLength] = useState<number | null>(null);
  const [pathViewMode, setPathViewMode] = useState<"table" | "graph">("graph");
  const [graphKey, setGraphKey] = useState(0);
  const [isLoadingPaths, setIsLoadingPaths] = useState(false);
  const [pathError, setPathError] = useState<string | null>(null);
  const [isPathGraphControlsOpen, setIsPathGraphControlsOpen] = useState(false);
  const [pathGraphLabelFontSize, setPathGraphLabelFontSize] = useState(9);
  const [pathGraphSearchGene, setPathGraphSearchGene] = useState<string>("none");
  const [pathGraphSelectedNodeLabel, setPathGraphSelectedNodeLabel] = useState<string | null>(null);
  const [pathGraphConnectedGenes, setPathGraphConnectedGenes] = useState<string[]>([]);
  const pathGraphSelectedNodeLabelRef = useRef<string | null>(null);

  useEffect(() => {
    pathGraphSelectedNodeLabelRef.current = pathGraphSelectedNodeLabel;
  }, [pathGraphSelectedNodeLabel]);

  const selectedCount = selectedGenes.size;
  const canFindPaths = selectedCount >= 2;
  const allSelected = geneList.length > 0 && selectedGenes.size === geneList.length;
  const noneSelected = selectedGenes.size === 0;

  const handleToggleGene = (gene: string) => {
    setSelectedGenes((prev) => {
      const next = new Set(prev);
      if (next.has(gene)) next.delete(gene);
      else next.add(gene);
      return next;
    });
  };

  const handleSelectAll = () => setSelectedGenes(new Set(geneList));
  const handleDeselectAll = () => setSelectedGenes(new Set());

  const handleFindKShortestPaths = useCallback(async () => {
    if (!canFindPaths) return;
    setPathError(null);
    setIsLoadingPaths(true);
    try {
      const data = await pathwayExplorerClient.getKHopNeighbors({
        edgetype: edgeType,
        selected_genes: Array.from(selectedGenes),
        edge_threshold: minEdgeScore,
      });
      setPathEdges(Array.isArray(data) ? data : []);
      setPathResultMaxLength(maxPathLength);
      setPathViewMode("graph");
      setIsPathGraphControlsOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load paths";
      setPathError(message);
      setPathEdges(null);
    } finally {
      setIsLoadingPaths(false);
    }
  }, [canFindPaths, selectedGenes, edgeType, minEdgeScore, maxPathLength]);

  // After Find K-Shortest Paths completes, scroll to Results section (header with table/graph toggle)
  useEffect(() => {
    const wasLoading = prevLoadingPathsRef.current;
    prevLoadingPathsRef.current = isLoadingPaths;
    if (wasLoading && !isLoadingPaths && pathEdges !== null) {
      if (resultsSectionRef.current) {
        requestAnimationFrame(() => {
          resultsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  }, [isLoadingPaths, pathEdges]);

  const pathNetworkData = useMemo(
    () => (pathEdges && pathEdges.length > 0 ? buildNetworkDataFromEdges(pathEdges) : null),
    [pathEdges]
  );

  const filteredPathEdges = useMemo(() => {
    if (!pathEdges) return [];
    const q = pathTableSearchQuery.trim().toLowerCase();
    if (!q) return pathEdges;
    return pathEdges.filter((row) => {
      const source = String(row.source ?? "").toLowerCase();
      const target = String(row.target ?? "").toLowerCase();
      const pathStr = Array.isArray(row.path) ? row.path.join(" ").toLowerCase() : "";
      const pathLen = String(row.path_length ?? "").toLowerCase();
      return source.includes(q) || target.includes(q) || pathStr.includes(q) || pathLen.includes(q);
    });
  }, [pathEdges, pathTableSearchQuery]);

  const handlePathTableFullscreen = useCallback(() => {
    const el = pathTableFullscreenRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleDownloadPathTableCsv = useCallback(() => {
    if (!pathEdges || pathEdges.length === 0) return;
    const header = "Source,Target,Path,Path length\n";
    const escape = (v: string) => {
      if (v.includes(",") || v.includes('"') || v.includes("\n")) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = pathEdges.map(
      (row) =>
        `${escape(String(row.source ?? ""))},${escape(String(row.target ?? ""))},${escape(Array.isArray(row.path) ? row.path.join(" → ") : "")},${row.path_length ?? ""}`
    );
    const csv = header + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "k-shortest-paths.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [pathEdges]);

  const pathCytoscapeElements = useMemo((): ElementDefinition[] => {
    if (!pathNetworkData) return [];
    const nodeElements: ElementDefinition[] = pathNetworkData.nodes.map((node) => ({
      data: { id: node.id, label: node.label },
    }));
    const nodeIdSet = new Set(pathNetworkData.nodes.map((n) => n.id));
    const edgeElements: ElementDefinition[] = pathNetworkData.edges
      .filter((e) => nodeIdSet.has(e.source) && nodeIdSet.has(e.target))
      .map((edge, index) => ({
        data: {
          id: `edge-${index}-${edge.source}-${edge.target}`,
          source: edge.source,
          target: edge.target,
          label: edgeType,
        },
      }));
    return [...nodeElements, ...edgeElements];
  }, [pathNetworkData, edgeType]);

  const handlePathGraphFullscreen = useCallback(() => {
    const wrapper = pathGraphFullscreenWrapperRef.current;
    if (!wrapper) return;
    if (!document.fullscreenElement) {
      wrapper.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handlePathGraphReset = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const layout = cy.layout({
      name: "cose",
      animate: true,
      animationDuration: 800,
      nodeDimensionsIncludeLabels: true,
      idealEdgeLength: 60,
      gravity: 1,
      gravityRange: 2.5,
    } as any);
    layout.run();
    layout.on("layoutstop", () => {
      cy.fit(undefined, 30);
      cy.center();
    });
  }, [cyRef]);

  const handlePathGraphDownload = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const bg = themeCtx.mode === "dark" ? "#1e293b" : "#f8fafc";
    const png = cy.png({ output: "blob", scale: 2, bg });
    const url = URL.createObjectURL(png);
    const link = document.createElement("a");
    link.href = url;
    link.download = "k-shortest-paths-network.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [cyRef, themeCtx.mode]);

  const pathGraphHighlightLabel = pathGraphSearchGene && pathGraphSearchGene !== "none" ? pathGraphSearchGene : null;
  const pathGraphStylesheet = useMemo(() => {
    const isDark = themeCtx.mode === "dark";
    /** Match ComparisonNetworkGraph gene-shared / gene-other / gene-gene edge tints */
    const geneCyanFill = isDark ? "#22d3ee" : "#06b6d4";
    const geneYellowFill = isDark ? "#fde047" : "#facc15";
    const edgeGeneCyanTint = isDark ? "#5eead4" : "#a5f3fc";
    const edgeGeneAmberTint = isDark ? "#fcd34d" : "#fde68a";
    const labelColor = isDark ? "#e2e8f0" : "#1e293b";
    /** Search focus: same cyan family with visible ring (comparison has no search state) */
    const nodeSearchBorder = isDark ? "#ecfeff" : "#0e7490";
    const edgeSearchWidth = 2;
    const edgeCyanWidth = 1.2;
    const edgeAmberWidth = 0.9;
    const isSearchGene = (label: string) => !!pathGraphHighlightLabel && label === pathGraphHighlightLabel;
    const touchesSelected = (src: string, tgt: string) => selectedGenes.has(src) || selectedGenes.has(tgt);

    const tapCenter = pathGraphSelectedNodeLabel;
    const inTapNeighborhood = (label: string) => {
      if (!tapCenter) return true;
      return label === tapCenter || pathGraphConnectedGenes.includes(label);
    };
    const edgeInTapNeighborhood = (src: string, tgt: string) => {
      if (!tapCenter) return false;
      return inTapNeighborhood(src) && inTapNeighborhood(tgt);
    };
    const tapFocusBorderCenter = isDark ? "#f8fafc" : "#0f172a";
    const tapFocusBorderNeighbor = isDark ? "#94a3b8" : "#475569";

    return [
      {
        selector: "node",
        style: {
          opacity: (ele: any) => {
            const label = ele.data("label");
            if (!tapCenter || isSearchGene(label)) return 1;
            return inTapNeighborhood(label) ? 1 : 0.22;
          },
          "background-color": (ele: any) => {
            const label = ele.data("label");
            if (isSearchGene(label)) return geneCyanFill;
            return selectedGenes.has(label) ? geneCyanFill : geneYellowFill;
          },
          "border-width": (ele: any) => {
            const label = ele.data("label");
            if (isSearchGene(label)) return 2;
            if (tapCenter && label === tapCenter) return 1;
            if (tapCenter && inTapNeighborhood(label)) return 1;
            return 0;
          },
          "border-color": (ele: any) => {
            const label = ele.data("label");
            if (isSearchGene(label)) return nodeSearchBorder;
            if (tapCenter && label === tapCenter) return tapFocusBorderCenter;
            if (tapCenter && inTapNeighborhood(label)) return tapFocusBorderNeighbor;
            return geneCyanFill;
          },
          "border-opacity": 1,
          shape: "ellipse",
          "border-style": "solid",
          width: (ele: any) => {
            const label = ele.data("label");
            if (isSearchGene(label)) return 18;
            if (tapCenter && label === tapCenter) return 20;
            if (tapCenter && inTapNeighborhood(label)) return 18;
            return selectedGenes.has(label) ? 18 : 16;
          },
          height: (ele: any) => {
            const label = ele.data("label");
            if (isSearchGene(label)) return 18;
            if (tapCenter && label === tapCenter) return 20;
            if (tapCenter && inTapNeighborhood(label)) return 18;
            return selectedGenes.has(label) ? 18 : 16;
          },
          padding: 0,
          label: "data(label)",
          /** Bottom anchor + negative Y margin places text above the ellipse (Cytoscape: top+positive pulls label down into the node). */
          "text-valign": "bottom",
          "text-halign": "center",
          "text-margin-y": (ele: any) => {
            const label = ele.data("label");
            const nodeH = isSearchGene(label)
              ? 18
              : tapCenter && label === tapCenter
                ? 20
                : tapCenter && inTapNeighborhood(label)
                  ? 18
                  : selectedGenes.has(label)
                    ? 18
                    : 16;
            const fs = Math.max(6, pathGraphLabelFontSize);
            /** Visible gap between bottom of label and top of node (Cytoscape + line-height needs extra slack). */
            const gapAboveNode = 6;
            const labelHeightApprox = Math.ceil(fs * 1.35);
            return -(Math.ceil(nodeH / 2) + labelHeightApprox + gapAboveNode);
          },
          color: labelColor,
          "font-size": `${Math.max(6, pathGraphLabelFontSize)}px`,
          "font-family": "system-ui, -apple-system, sans-serif",
          "font-weight": 400,
          "text-wrap": "wrap",
          "text-max-width": "80px",
        } as any,
      },
      {
        selector: "node:active",
        style: {
          "overlay-color": isDark ? "#64748b" : "#94a3b8",
          "overlay-padding": 8,
          "overlay-opacity": 0.2,
        } as any,
      },
      {
        selector: "node.dragging",
        style: {
          "border-width": 1.5,
          "border-color": isDark ? "#67e8f9" : "#0891b2",
          "background-color": isDark ? "#155e75" : "#cffafe",
        } as any,
      },
      {
        selector: "edge",
        style: {
          width: (ele: any) => {
            const srcLabel = ele.source().data("label");
            const tgtLabel = ele.target().data("label");
            const touchesSearch = pathGraphHighlightLabel && (srcLabel === pathGraphHighlightLabel || tgtLabel === pathGraphHighlightLabel);
            if (tapCenter) {
              if (edgeInTapNeighborhood(srcLabel, tgtLabel)) return 2.4;
              return 0.5;
            }
            if (touchesSearch) return edgeSearchWidth;
            if (touchesSelected(srcLabel, tgtLabel)) return edgeCyanWidth;
            return edgeAmberWidth;
          },
          "line-color": (ele: any) => {
            const srcLabel = ele.source().data("label");
            const tgtLabel = ele.target().data("label");
            const touchesSearch = pathGraphHighlightLabel && (srcLabel === pathGraphHighlightLabel || tgtLabel === pathGraphHighlightLabel);
            if (tapCenter) {
              if (edgeInTapNeighborhood(srcLabel, tgtLabel)) return edgeGeneCyanTint;
              return isDark ? "#475569" : "#94a3b8";
            }
            if (touchesSearch || touchesSelected(srcLabel, tgtLabel)) return edgeGeneCyanTint;
            return edgeGeneAmberTint;
          },
          "line-opacity": (ele: any) => {
            const srcLabel = ele.source().data("label");
            const tgtLabel = ele.target().data("label");
            const touchesSearch = pathGraphHighlightLabel && (srcLabel === pathGraphHighlightLabel || tgtLabel === pathGraphHighlightLabel);
            if (tapCenter) {
              return edgeInTapNeighborhood(srcLabel, tgtLabel) ? 1 : 0.12;
            }
            if (touchesSearch || touchesSelected(srcLabel, tgtLabel)) return 0.9;
            return 0.75;
          },
          "target-arrow-shape": "none",
          "curve-style": "straight",
        } as any,
      },
    ];
  }, [
    themeCtx.mode,
    selectedGenes,
    pathGraphLabelFontSize,
    pathGraphHighlightLabel,
    pathGraphSelectedNodeLabel,
    pathGraphConnectedGenes,
  ]);

  useEffect(() => {
    layoutInitialized.current = false;
  }, [pathCytoscapeElements.length]);

  useEffect(() => {
    const c = cyRef.current;
    if (!c) return;
    const t = setTimeout(() => {
      const cy = cyRef.current;
      if (!cy) return;
      cy.resize();
      if (isPathGraphFullscreen) {
        cy.fit(undefined, 50);
        cy.center();
      } else {
        const layout = cy.layout({
          name: "cose",
          animate: true,
          animationDuration: 400,
          nodeDimensionsIncludeLabels: true,
          idealEdgeLength: 60,
          gravity: 1,
          gravityRange: 2.5,
        } as any);
        layout.run();
        layout.on("layoutstop", () => {
          cy.fit(undefined, 30);
          cy.center();
        });
      }
    }, isPathGraphFullscreen ? 100 : 150);
    return () => clearTimeout(t);
  }, [isPathGraphFullscreen]);

  // Resize and refit graph when container size changes (drawer open/close, fullscreen, window resize)
  useEffect(() => {
    const container = pathGraphContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const cy = cyRef.current;
          if (cy) {
            cy.resize();
            cy.fit(undefined, 30);
            cy.center();
          }
        }, 50);
      });
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // When drawer opens/closes, container size changes; trigger resize+fit after transition
  useEffect(() => {
    const t = setTimeout(() => {
      const cy = cyRef.current;
      if (cy) {
        cy.resize();
        cy.fit(undefined, 30);
        cy.center();
      }
    }, 350);
    return () => clearTimeout(t);
  }, [isPathGraphControlsOpen]);

  return (
    <div className="space-y-4 p-6 min-w-0 max-w-full">
      <div className="mb-4">
        <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
          <p className="text-xs md:text-sm text-gray-700 dark:text-gray-200">
            Discover indirect connections by selecting two or more genes from the pathway. The algorithm finds paths up to length k (hops) connecting your selected genes. Note: The visualization shows only the edges along the shortest paths between selected genes; direct connections between intermediate genes are omitted for clarity. View results in either table or network format using the tabs below.
          </p>
        </div>
      </div>

      {geneList.length === 0 ? (
        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg p-8 text-center">
          <p className="text-slate-500 dark:text-slate-400">
            Plot a network first to see genes from the selected pathway. Then switch back here to select genes for K-Shortest Paths.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                Select at least 2 genes:
              </h3>
              <div className="group relative">
                <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-56 text-left">
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                    Click a gene to select or deselect it. Choose at least 2 genes to find paths between them.
                    <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-2 flex-nowrap shrink-0">
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={allSelected}
                className="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleDeselectAll}
                disabled={noneSelected}
                className="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                Deselect All
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {geneList.map((gene) => {
              const isSelected = selectedGenes.has(gene);
              return (
                <button
                  key={gene}
                  type="button"
                  onClick={() => handleToggleGene(gene)}
                  className={`px-3.5 py-2 text-sm font-medium rounded-full border transition-all duration-200 ${isSelected
                    ? "bg-blue-500 border-blue-500 text-white shadow-sm hover:bg-blue-600 hover:border-blue-600"
                    : "bg-gray-100 dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-200 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-200 dark:hover:bg-slate-600"
                    }`}
                >
                  {gene}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-4 pt-2">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <span>Max path length:</span>
                <div className="group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-52 text-left">
                    <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                      Maximum number of hops (edges) between selected genes. Increase to find longer paths.
                      <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                    </div>
                  </div>
                </div>
              </label>
              <div className="flex items-center bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setMaxPathLength((k) => Math.max(MIN_PATH_LENGTH, k - 1))}
                  disabled={maxPathLength <= MIN_PATH_LENGTH}
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-r border-gray-200 dark:border-slate-600"
                >
                  <span className="text-lg font-bold">−</span>
                </button>
                <div className="flex-1 min-w-[3rem] text-center py-2">
                  <span className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {maxPathLength}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setMaxPathLength((k) => Math.min(MAX_PATH_LENGTH, k + 1))}
                  disabled={maxPathLength >= MAX_PATH_LENGTH}
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-l border-gray-200 dark:border-slate-600"
                >
                  <span className="text-lg font-bold">+</span>
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={handleFindKShortestPaths}
              disabled={!canFindPaths || isLoadingPaths}
              className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white shadow-sm transition-colors"
            >
              {isLoadingPaths ? "Loading…" : "Find K-Shortest Paths"}
            </button>
          </div>

          {pathError && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {pathError}
            </div>
          )}

          {pathEdges !== null && (
            <div ref={resultsSectionRef} className="space-y-4 pt-4 border-t border-gray-200 dark:border-slate-600 min-w-0 overflow-x-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  Results ({pathEdges.length} path{pathEdges.length !== 1 ? "s" : ""})
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">View:</span>
                  <div className="flex rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-gray-50 dark:bg-slate-700">
                    <button
                      type="button"
                      onClick={() => {
                        layoutInitialized.current = false;
                        setGraphKey((k) => k + 1);
                        setPathViewMode("graph");
                        setIsPathGraphControlsOpen(true);
                      }}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors ${pathViewMode === "graph"
                        ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                        }`}
                    >
                      Network graph
                    </button>
                    <button
                      type="button"
                      onClick={() => setPathViewMode("table")}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 dark:border-slate-600 ${pathViewMode === "table"
                        ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                        }`}
                    >
                      Path table
                    </button>
                  </div>
                </div>
              </div>

              {pathViewMode === "table" ? (
                <div
                  ref={pathTableFullscreenRef}
                  className={`relative rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden min-w-0 max-w-full ${isPathTableFullscreen ? "border-0" : "w-full"}`}
                  style={{
                    width: isPathTableFullscreen ? "100vw" : undefined,
                    height: isPathTableFullscreen ? "100vh" : undefined,
                    minHeight: isPathTableFullscreen ? "100vh" : undefined,
                  }}
                >
                  <div className="flex flex-wrap items-center justify-end gap-2 p-2 border-b border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 min-w-0 shrink-0">
                    <div className="relative min-w-[180px] max-w-sm">
                      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search table..."
                        value={pathTableSearchQuery}
                        onChange={(e) => setPathTableSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadPathTableCsv}
                      title="Download as CSV"
                      className="p-2 rounded-lg border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handlePathTableFullscreen}
                      title={isPathTableFullscreen ? "Exit fullscreen" : "Fullscreen"}
                      className="p-2 rounded-lg border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                    >
                      {isPathTableFullscreen ? (
                        <ArrowsPointingInIcon className="h-4 w-4" />
                      ) : (
                        <ArrowsPointingOutIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <div className="overflow-x-auto overflow-y-auto min-w-0" style={isPathTableFullscreen ? { height: "calc(100vh - 52px)" } : undefined}>
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600">
                      <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                            Source
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                            Target
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                            Path
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                            Path length
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
                        {filteredPathEdges.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                              {pathTableSearchQuery.trim() ? `No rows match "${pathTableSearchQuery.trim()}".` : "No path data."}
                            </td>
                          </tr>
                        ) : (
                          filteredPathEdges.map((row, i) => (
                            <tr key={i}>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                {row.source}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                {row.target}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 font-mono">
                                {Array.isArray(row.path) ? row.path.join(" → ") : ""}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                                {row.path_length}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : pathNetworkData && pathCytoscapeElements.length > 0 ? (
                <div className="space-y-2 w-full min-w-0 max-w-full overflow-x-hidden">
                  <div
                    ref={pathGraphFullscreenWrapperRef}
                    tabIndex={-1}
                    aria-label="K-Shortest Paths network graph"
                    className={`relative rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-slate-50 dark:bg-slate-800/95 box-border min-w-0 outline-none ${isPathGraphFullscreen ? "bg-white dark:bg-slate-900" : ""}`}
                    style={{
                      width: isPathGraphFullscreen ? "100vw" : "100%",
                      maxWidth: isPathGraphFullscreen ? "none" : "100%",
                      height: isPathGraphFullscreen ? "100vh" : 540,
                      minHeight: isPathGraphFullscreen ? "100vh" : 540,
                    }}
                  >
                    <div
                      ref={pathGraphContainerRef}
                      className="absolute inset-0 top-0 left-0 transition-all duration-300 min-w-0"
                      style={{
                        right: isPathGraphControlsOpen ? "320px" : "0",
                        bottom: 0,
                        width: isPathGraphControlsOpen ? "calc(100% - 320px)" : "100%",
                        maxWidth: "100%",
                      }}
                    >
                      <div className="absolute top-2 left-2 z-30 flex flex-col gap-1">
                        <div
                          className="bg-black bg-opacity-70 text-white text-xs font-medium px-2 py-1.5 rounded max-w-[min(320px,70vw)] truncate pointer-events-none"
                          title={`K-Shortest Paths Network (k=${pathResultMaxLength ?? maxPathLength})`}
                        >
                          K-Shortest Paths Network (k={pathResultMaxLength ?? maxPathLength})
                        </div>
                        {pathGraphSelectedNodeLabel != null && (
                          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-w-[min(320px,70vw)] overflow-hidden pointer-events-auto">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 dark:border-slate-700">
                              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                Gene: {pathGraphSelectedNodeLabel}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setPathGraphSelectedNodeLabel(null);
                                  setPathGraphConnectedGenes([]);
                                }}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-gray-400"
                                aria-label="Close"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="px-3 py-2 max-h-40 overflow-y-auto">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                                Connected genes ({pathGraphConnectedGenes.length}):
                              </p>
                              {pathGraphConnectedGenes.length === 0 ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400 italic">None</p>
                              ) : (
                                <p className="text-xs text-gray-700 dark:text-gray-200 break-words">
                                  {pathGraphConnectedGenes.join(", ")}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="absolute top-2 right-2 z-30 flex items-center gap-1">
                        <button
                          onClick={handlePathGraphReset}
                          title="Reset layout"
                          className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                        >
                          <ArrowPathIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        <button
                          onClick={handlePathGraphDownload}
                          title="Download as PNG"
                          className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        <button
                          onClick={handlePathGraphFullscreen}
                          title={isPathGraphFullscreen ? "Exit fullscreen" : "Fullscreen"}
                          className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                        >
                          {isPathGraphFullscreen ? (
                            <ArrowsPointingInIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                          ) : (
                            <ArrowsPointingOutIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                          )}
                        </button>
                      </div>
                      <CytoscapeComponent
                        key={`path-graph-${graphKey}-${pathCytoscapeElements.length}`}
                        elements={pathCytoscapeElements}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          width: "100%",
                          height: "100%",
                          boxSizing: "border-box",
                        }}
                        stylesheet={pathGraphStylesheet}
                        cy={(cy: Core) => {
                          cyRef.current = cy;
                          cy.nodes().ungrabify();
                          cy.nodes().grabify();
                          cy.on("tap", (evt: any) => {
                            if (evt.target === cy) {
                              setPathGraphSelectedNodeLabel(null);
                              setPathGraphConnectedGenes([]);
                            }
                          });
                          cy.on("tap", "node", (evt: any) => {
                            const node = evt.target;
                            const label = node.data("label");
                            if (pathGraphSelectedNodeLabelRef.current === label) {
                              setPathGraphSelectedNodeLabel(null);
                              setPathGraphConnectedGenes([]);
                              return;
                            }
                            const neighbors = node.neighborhood("node");
                            const labels = neighbors.map((n: any) => n.data("label")).filter(Boolean);
                            setPathGraphSelectedNodeLabel(label);
                            setPathGraphConnectedGenes(labels);
                          });
                          cy.on("grab", "node", (evt: any) => {
                            evt.target.addClass("dragging");
                          });
                          cy.on("free", "node", (evt: any) => {
                            evt.target.removeClass("dragging");
                          });
                          if (!layoutInitialized.current) {
                            const layout = cy.layout({
                              name: "cose",
                              animate: true,
                              animationDuration: 800,
                              nodeDimensionsIncludeLabels: true,
                              idealEdgeLength: 60,
                              gravity: 1,
                              gravityRange: 2.5,
                            } as any);
                            layout.run();
                            layout.on("layoutstop", () => {
                              layoutInitialized.current = true;
                              cy.fit(undefined, 30);
                              cy.center();
                            });
                          }
                        }}
                        wheelSensitivity={0.2}
                        autoungrabify={false}
                        autounselectify={false}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsPathGraphControlsOpen(!isPathGraphControlsOpen)}
                      className="absolute top-1/2 -translate-y-1/2 z-40 flex items-center justify-center w-7 h-16 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 text-white shadow-lg hover:shadow-xl transition-all duration-300 group rounded-l-md"
                      style={{ right: isPathGraphControlsOpen ? "320px" : "0" }}
                      title={isPathGraphControlsOpen ? "Hide controls" : "Show controls"}
                    >
                      {isPathGraphControlsOpen ? (
                        <ChevronUpIcon className="h-4 w-4 transform rotate-90 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 transform rotate-90 group-hover:translate-x-0.5 transition-transform duration-200" />
                      )}
                    </button>

                    <div
                      className={`absolute top-0 h-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 border-l shadow-2xl transition-all duration-300 ease-in-out z-20 overflow-hidden ${isPathGraphControlsOpen ? "border-l-0 rounded-r-lg" : "rounded-lg"
                        }`}
                      style={{
                        width: "320px",
                        right: isPathGraphControlsOpen ? "0" : "-320px",
                        opacity: isPathGraphControlsOpen ? 1 : 0,
                        pointerEvents: isPathGraphControlsOpen ? "auto" : "none",
                      }}
                    >
                      <div className="relative flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 z-20">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
                            <InformationCircleIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                              K-Path Plot Controls
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Adjust visualization settings for the k-shortest paths network
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className="relative p-5 space-y-6 overflow-y-auto bg-gray-50 dark:bg-slate-800/50 z-10"
                        style={{ height: "calc(100% - 73px)" }}
                      >
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              Search Gene
                            </label>
                            <div className="group relative">
                              <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 w-56 text-left">
                                <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                                  Highlight a gene node in the network
                                  <div className="absolute top-full right-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="relative">
                            <select
                              value={pathGraphSearchGene}
                              onChange={(e) => setPathGraphSearchGene(e.target.value)}
                              className="w-full pl-3 pr-8 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm appearance-none"
                            >
                              <option value="none">None</option>
                              {pathNetworkData.nodes.map((node) => (
                                <option key={node.id} value={node.label}>
                                  {node.label}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              Label Font Size
                            </label>
                            <div className="group relative">
                              <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 w-48 text-left">
                                <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                                  Adjust node label font size
                                  <div className="absolute top-full right-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setPathGraphLabelFontSize((s) => Math.max(6, s - 1))}
                              className="flex-1 py-2 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors flex items-center justify-center border-r border-gray-200 dark:border-slate-600"
                            >
                              <span className="text-gray-600 dark:text-gray-300 font-bold text-lg">−</span>
                            </button>
                            <div className="flex-1 py-2 text-center bg-gray-50 dark:bg-slate-700/50">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {pathGraphLabelFontSize}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPathGraphLabelFontSize((s) => Math.min(24, s + 1))}
                              className="flex-1 py-2 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors flex items-center justify-center border-l border-gray-200 dark:border-slate-600"
                            >
                              <span className="text-gray-600 dark:text-gray-300 font-bold text-lg">+</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                  No edges to display in graph view.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
